import React from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogIn, LogOut, Shield, Bell, Crown, BellRing, Loader2, ExternalLink, Receipt } from "lucide-react";
import { API, LANGUAGES, useApp } from "@/state/AppContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import usePush from "@/hooks/usePush";
import CancelFlowModal from "@/components/CancelFlowModal";
import InvoicesModal from "@/components/InvoicesModal";
import LoyaltyCard from "@/components/LoyaltyCard";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, setToken, language, setLanguage, authHeaders } = useApp();
  const [portalBusy, setPortalBusy] = React.useState(false);
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [invoicesOpen, setInvoicesOpen] = React.useState(false);

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const r = await axios.post(`${API}/payments/portal`, {
        return_url: window.location.origin + "/profile",
      }, { headers: authHeaders });
      window.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Abo-Portal konnte nicht geöffnet werden.");
      setPortalBusy(false);
    }
  };

  return (
    <div className="px-5 pt-6 pb-8 space-y-5" data-testid="profile-page">
      <h1 className="text-2xl font-bold text-slate-900">Profil</h1>

      {!user ? (
        <div className="rounded-3xl bg-blue-50/60 border border-blue-100 p-5 text-center">
          <p className="text-slate-800">Melde dich an, um deine Briefe zu speichern und Erinnerungen zu erhalten.</p>
          <button onClick={() => navigate("/auth")} data-testid="profile-login-btn"
            className="mt-3 inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-blue-600 text-white font-semibold">
            <LogIn className="w-5 h-5" /> Anmelden / Registrieren
          </button>
        </div>
      ) : (
        <div className="rounded-3xl bg-white border border-slate-200 p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Persönliche Einstellungen</div>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-slate-600">Name</label>
              <Input defaultValue={user.name} data-testid="profile-name" className="mt-1 h-11 rounded-xl" />
            </div>
            <div>
              <label className="text-sm text-slate-600">E-Mail</label>
              <Input defaultValue={user.email} disabled data-testid="profile-email" className="mt-1 h-11 rounded-xl bg-slate-50" />
            </div>
          </div>
        </div>
      )}

      <div className="rounded-3xl bg-white border border-slate-200 p-4 shadow-sm">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Erklärungssprache</div>
        <Select value={language} onValueChange={setLanguage}>
          <SelectTrigger data-testid="profile-lang" className="h-12 rounded-xl bg-blue-50 border-blue-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LANGUAGES.map(l => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-3xl bg-white border border-slate-200 p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3"><Shield className="w-5 h-5 text-blue-600" />
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Datenschutz</div>
        </div>
        <SettingRow label="Dokumente nach Analyse automatisch löschen" defaultChecked testid="privacy-autodelete" />
        <SettingRow label={'Dokumente in „Meine Briefe" speichern'} testid="privacy-save" />
      </div>

      <div className="rounded-3xl bg-white border border-slate-200 p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3"><Bell className="w-5 h-5 text-blue-600" />
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Erinnerungen</div>
        </div>
        <PushRow />
        <SettingRow label="E-Mail-Erinnerungen" testid="reminders-email" />
      </div>

      <div className="rounded-3xl bg-blue-50/60 border border-blue-100 p-4">
        <div className="flex items-center gap-2 mb-2"><Crown className="w-5 h-5 text-blue-600" />
          <div className="text-sm font-semibold text-blue-900">Abo</div>
        </div>
        <p className="text-sm text-slate-700">Aktuell: <b>{user?.subscription || "Kostenlos"}</b></p>        {user && (user.subscription === "plus" || user.subscription === "premium") ? (
          <div className="mt-3 flex flex-col gap-2">
            <button
              onClick={openPortal}
              disabled={portalBusy}
              data-testid="manage-subscription-btn"
              className="min-h-[44px] rounded-xl bg-blue-600 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {portalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              Zahlungsmethode ändern
            </button>
            <button
              onClick={() => setInvoicesOpen(true)}
              data-testid="invoices-btn"
              className="min-h-[44px] rounded-xl bg-blue-50 text-blue-700 text-sm font-semibold flex items-center justify-center gap-2 border border-blue-100"
            >
              <Receipt className="w-4 h-4" /> Rechnungen anzeigen
            </button>
            <button
              onClick={() => setCancelOpen(true)}
              data-testid="cancel-subscription-btn"
              className="min-h-[44px] rounded-xl bg-white text-slate-600 text-sm font-semibold border border-slate-200"
            >
              Abo kündigen
            </button>
          </div>
        ) : (
          <button onClick={() => navigate("/pricing")} data-testid="profile-pricing-btn"
            className="mt-3 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold">Pläne ansehen</button>
        )}
      </div>

      <LoyaltyCard />

      <CancelFlowModal open={cancelOpen} onClose={() => setCancelOpen(false)} />
      <InvoicesModal open={invoicesOpen} onClose={() => setInvoicesOpen(false)} />

      {user && (
        <button onClick={() => { setToken(""); navigate("/"); }} data-testid="profile-logout"
          className="w-full min-h-[54px] rounded-2xl bg-white border border-slate-200 text-slate-700 font-semibold flex items-center justify-center gap-2">
          <LogOut className="w-5 h-5" /> Abmelden
        </button>
      )}

      <p className="text-center text-xs text-slate-400 mt-6">
        BriefAssist v1 ·{" "}
        <button
          onClick={() => navigate("/impressum")}
          data-testid="profile-impressum-link"
          className="underline underline-offset-2 hover:text-slate-600"
        >
          Impressum
        </button>{" "}
        ·{" "}
        <button
          onClick={() => navigate("/datenschutz")}
          data-testid="profile-datenschutz-link"
          className="underline underline-offset-2 hover:text-slate-600"
        >
          Datenschutz
        </button>{" "}
        ·{" "}
        <button
          onClick={() => navigate("/agb")}
          data-testid="profile-agb-link"
          className="underline underline-offset-2 hover:text-slate-600"
        >
          AGB
        </button>
      </p>
    </div>
  );
}

function SettingRow({ label, defaultChecked, testid }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-[15px] text-slate-800 pr-4">{label}</span>
      <Switch defaultChecked={defaultChecked} data-testid={testid} />
    </div>
  );
}

function PushRow() {
  const push = usePush();
  const { user } = useApp();
  const [busy, setBusy] = React.useState(false);

  if (!push.supported) {
    return (
      <div className="flex items-center justify-between py-2 opacity-60">
        <span className="text-[15px] text-slate-800 pr-4">Push-Benachrichtigungen (Browser nicht unterstützt)</span>
      </div>
    );
  }

  const denied = push.permission === "denied";
  const onToggle = async (checked) => {
    if (!user) { toast.info("Bitte zuerst anmelden."); return; }
    setBusy(true);
    try {
      if (checked) {
        await push.subscribe();
        toast.success("Push aktiviert.");
      } else {
        await push.unsubscribe();
        toast.success("Push deaktiviert.");
      }
    } catch (e) {
      toast.error(e?.message || "Push konnte nicht aktiviert werden.");
    } finally { setBusy(false); }
  };

  return (
    <div className="py-2" data-testid="push-row">
      <div className="flex items-center justify-between">
        <div className="pr-4">
          <div className="text-[15px] text-slate-800">Push-Benachrichtigungen</div>
          {denied && (
            <div className="text-xs text-red-600 mt-0.5">Blockiert im Browser. Bitte in den Browser-Einstellungen erlauben.</div>
          )}
        </div>
        <Switch
          checked={push.subscribed}
          onCheckedChange={onToggle}
          disabled={busy || denied}
          data-testid="reminders-push"
        />
      </div>
      {push.subscribed && (
        <button
          onClick={async () => {
            try { await push.testPush(); toast.success("Test-Push gesendet."); }
            catch { toast.error("Test-Push fehlgeschlagen."); }
          }}
          data-testid="push-test-btn"
          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
          Test-Benachrichtigung senden
        </button>
      )}
    </div>
  );
}
